import { isAfter, startOfDay } from 'date-fns'
import { useRouter } from 'next/router'
import { useEffect, useMemo, useState } from 'react'
import { useQueryClient } from 'react-query'
import Link from 'next/link'

import { Button } from '@wartek-id/button-v1'
import { Checkbox, CheckboxGroup } from '@wartek-id/checkbox-v1'
import { Tooltip } from '@wartek-id/tooltip'
import { CollapsibleIcon } from '@wartek-id/collapsible'
import {
  CollapsibleContent,
  CollapsibleTrigger,
} from '@wartek-id/collapsible-v1'
import {
  Dialog,
  DialogCloseButton,
  DialogContent,
  DialogHeader,
  DialogOverlay,
} from '@wartek-id/dialog-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Shimmer } from '@wartek-id/shimmer'
import { Text } from '@wartek-id/text-v1'
import { useToast } from '@wartek-id/toast-v2'

import { InvalidUserNoAccessPelaksanaan } from 'api/kinerja/kepala-sekolah/config'
import {
  API_PATH_PREDIKAT_KINERJA_STEP,
  useAPIGetPelaksanaanConfig,
  useAPIGetPenetapanPredikatKinerjaStep,
  useAPIGetPredikatKinerjaList,
  useAPIUpsertPredikatKinerjaList,
} from 'api/kinerja/useAPIKinerja'
import useDemoContext from 'app/KinerjaPage/hooks/useDemoContext'
import { Header } from 'app/KinerjaPage/pages/PelaksanaanV2/components/Header'
import { TablePenilaianKinerja } from 'app/KinerjaPage/pages/PelaksanaanV2/components/TablePenilaianKinerja'
import { PREDICATE_RECOMMENDATION_RESULT_TEXT } from 'app/KinerjaPage/pages/PelaksanaanV2/constants/predicateRecommendation'
import { CardCollapsible } from 'app/KinerjaShared/components/CardCollapsible'
import CardDeadlinePeriod from 'app/KinerjaShared/components/CardDeadlinePeriod'
import DialogSaveDraft from 'app/KinerjaShared/components/DialogSaveDraft'
import {
  PENGELOLAAN_KINERJA_KS_PEGAWAI_PATH,
  PENGELOLAAN_KINERJA_KS_PENILAI_PATH,
} from 'app/KinerjaShared/constants/path'
import { formatDate, isDateValid } from 'utils/datetime'
import useSKPPelaksanaanTrackEvent from 'app/KinerjaPage/hooks/useSKPPelaksanaanTrackEvent'
import { assetUrl } from 'utils/url'
import { PANDUAN_PENGELOLAAN_URL } from 'app/KinerjaShared/utils/panduan'
import { useIsPrior2025 } from 'app/KinerjaShared/hooks/useIsPrior2025'
import { API_PATH_PREDIKAT_KINERJA_LIST } from 'api/kinerja/useAPIKinerja'

import type {
  PredikatKinerjaItem,
  TPredicateRecommendationResult,
} from 'types/kinerja-pelaksanaan'

type RangkumanPenilaianKinerjaProps = {
  npsn: string
}

export function RangkumanPenilaianKinerja({
  npsn,
}: RangkumanPenilaianKinerjaProps) {
  const { trackEvent } = useSKPPelaksanaanTrackEvent()
  const router = useRouter()
  const toast = useToast()
  const queryClient = useQueryClient()
  const { isDemoMode } = useDemoContext()
  const isPriorTo2025 = useIsPrior2025()

  const [completedItems, setCompletedItems] = useState<
    Array<PredikatKinerjaItem>
  >([])
  const [isDirty, setIsDirty] = useState(false)
  const [activeDialog, setActiveDialog] = useState<
    'NONE' | 'SUBMISSION' | 'SAVE_TO_DRAFT' | 'SUBMISSION_SUCCESS'
  >('NONE')

  const {
    data: dataPelaksanaanConfig,
    isSuccess: isSuccessPelaksanaanConfig,
    isFetching: isLoadingPelaksanaanConfig,
  } = useAPIGetPelaksanaanConfig({
    params: { key: 'PENETAPAN_PREDIKAT_KINERJA' },
    options: {
      staleTime: 60 * 60 * 1000, // 1 hour
    },
  })

  const isPenetapanPredikatKinerjaStarted = useMemo(() => {
    // Deadline date
    const startDate =
      dataPelaksanaanConfig?.data?.predicatePerformanceAccessDate
    const isValidDate = isDateValid(startDate)
    const isStarted =
      isValidDate && isAfter(new Date(), startOfDay(new Date(startDate)))

    return isStarted
  }, [dataPelaksanaanConfig])

  const {
    data: dataPenetapanStep,
    isSuccess: isSuccessPenetapanStep,
    isLoading: isLoadingPenetapanStep,
  } = useAPIGetPenetapanPredikatKinerjaStep({
    params: {
      npsn,
    },
    options: {
      useErrorBoundary: false,
      staleTime: 60 * 60 * 1000, // 1 hour
      enabled: isPenetapanPredikatKinerjaStarted,
    },
    isDemoMode,
  })

  const submissionStep = dataPenetapanStep?.data?.submissionStep
  const isRangkumanPenetapanSent = submissionStep !== 'NOT_CREATED'

  const { isFetching: isLoadingGetList, data: dataGetList } =
    useAPIGetPredikatKinerjaList({
      params: { npsn },
      options: {
        staleTime: 60 * 60 * 1000, // 1 hour
        enabled:
          !isRangkumanPenetapanSent &&
          isSuccessPenetapanStep &&
          isPenetapanPredikatKinerjaStarted,
      },
      isDemoMode,
    })

  const upsertPredikatKinerjaMutation = useAPIUpsertPredikatKinerjaList(
    npsn,
    {},
    isDemoMode
  )

  const handleSaveToDraft = () => {
    upsertPredikatKinerjaMutation.mutate(
      {
        submissionStep: 'SUBMIT_KADIS',
        state: 'DRAFT',
        data: completedItems
          // Only send items that fully rated to prevent 400 error
          .filter(
            (item) =>
              !!item.workBehavior?.result && !!item.performancePractice?.result
          )
          .map((item) => ({
            ptkId: item.ptkId,
            workBehavior: item.workBehavior?.result,
            performancePractice: item.performancePractice?.result,
          })),
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(
            `${API_PATH_PREDIKAT_KINERJA_LIST}/${npsn}`
          )

          router.replace(
            `${PENGELOLAAN_KINERJA_KS_PENILAI_PATH}/penilaian-kinerja/${npsn}/daftar-guru`
          )
          toast({
            message: 'Berhasil simpan draf!',
            type: 'success',
          })
        },
        onError: () => {
          router.replace(
            `${PENGELOLAAN_KINERJA_KS_PENILAI_PATH}/penilaian-kinerja/${npsn}/daftar-guru`
          )
          toast({
            message: 'Gagal simpan draf. Coba lagi.',
          })
        },
      }
    )
  }

  const handleSubmission = () => {
    upsertPredikatKinerjaMutation.mutate(
      {
        submissionStep: 'SUBMIT_KADIS',
        state: 'SUBMITTED',
        data: completedItems.map((item) => ({
          ptkId: item.ptkId,
          workBehavior: item.workBehavior?.result,
          performancePractice: item.performancePractice?.result,
        })),
      },
      {
        onSuccess: () => {
          setActiveDialog('SUBMISSION_SUCCESS')
        },
        onError: () => {
          router.replace(
            `${PENGELOLAAN_KINERJA_KS_PENILAI_PATH}/penilaian-kinerja/${npsn}/daftar-guru`
          )
          toast({
            message: 'Gagal lanjut ke Penetapan Predikat Kinerja. Coba lagi.',
          })
        },
      }
    )
  }

  // Deadline date
  const deadlineDate =
    dataPelaksanaanConfig?.data?.predicatePerformanceDeadlineDate
  const isValidDeadlineDate = isDateValid(deadlineDate)
  const formattedDeadlineDate = isValidDeadlineDate
    ? formatDate(deadlineDate)
    : '-'

  const predikatKinerjaCounts: Record<TPredicateRecommendationResult, number> =
    useMemo(() => {
      const counts = {
        VERY_GOOD: 0,
        GOOD: 0,
        NEED_IMPROVEMENT: 0,
        BAD: 0,
        VERY_BAD: 0,
      }

      completedItems.forEach((item) => {
        if (item.predicateRecommendation) {
          counts[item.predicateRecommendation]++
        }
      })

      return counts
    }, [completedItems])

  const uncompletedItems = dataGetList?.data?.notCompletedUser ?? []

  const hasPartiallyRatedItems = useMemo(() => {
    return completedItems.some(
      (item) =>
        (item.workBehavior?.result && !item.performancePractice?.result) ||
        (!item.workBehavior?.result && item.performancePractice?.result)
    )
  }, [completedItems])

  const isLoadingAllData =
    isLoadingPelaksanaanConfig || isLoadingPenetapanStep || isLoadingGetList

  // Sync remote data with local state
  useEffect(() => {
    // Filter only items that are allowed to be sent to Dinas Pendidikan
    const eligibleItems =
      dataGetList?.data?.completedUser?.filter(
        (item) => item.ableSubmit === true
      ) ?? []

    const resolvedItems = isPriorTo2025
      ? eligibleItems
      : // On 2025 onwards, the manual result is used instead of the system recommendation result
        eligibleItems.map((item) => ({
          ...item,
          workBehavior: {
            ...item.workBehavior,
            result: item.workBehavior?.manualResult,
          },
          performancePractice: {
            ...item.performancePractice,
            result: item.performancePractice?.manualResult,
          },
          predicateRecommendation: item.predicateManual,
        }))

    setCompletedItems(resolvedItems)
  }, [dataGetList, isPriorTo2025])

  // Redirect to Daftar Guru if the data is sent
  useEffect(() => {
    if (
      isRangkumanPenetapanSent &&
      isSuccessPenetapanStep &&
      !upsertPredikatKinerjaMutation.isSuccess // Make sure this doesnt run upon cache invalidation
    ) {
      router.replace(
        `${PENGELOLAAN_KINERJA_KS_PENILAI_PATH}/penilaian-kinerja/${npsn}/daftar-guru`
      )
      toast({
        message: 'Predikat sudah terkirim',
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isRangkumanPenetapanSent,
    isSuccessPenetapanStep,
    upsertPredikatKinerjaMutation.isSuccess,
  ])

  // Show no access error if the Penetapan date is not started
  useEffect(() => {
    if (!isPenetapanPredikatKinerjaStarted && isSuccessPelaksanaanConfig) {
      throw new InvalidUserNoAccessPelaksanaan('path', null)
    }
  }, [isPenetapanPredikatKinerjaStarted, isSuccessPelaksanaanConfig])

  const isAllItemsRated = useMemo(() => {
    return completedItems.every((item) => !!item.predicateRecommendation)
  }, [completedItems])

  const isSubmitDisabled =
    upsertPredikatKinerjaMutation.isLoading ||
    isLoadingAllData ||
    completedItems.length === 0 ||
    !isAllItemsRated

  const isDraftDisabled =
    hasPartiallyRatedItems ||
    upsertPredikatKinerjaMutation.isLoading ||
    isLoadingAllData ||
    !isDirty

  return (
    <>
      <main className="px-8 pb-8 pt-12 bg-gray-10 min-h-full">
        <div className="max-w-7xl mx-auto space-y-5">
          <Header
            title="Kirim Data Penilaian Kinerja ke Dinas Pendidikan"
            className="mb-9"
            rightNode={<FaqsButton />}
            onBack={() => {
              if (!isDraftDisabled) {
                setActiveDialog('SAVE_TO_DRAFT')
              } else {
                router.back()
              }
            }}
          />

          <section>
            <div className="rounded-t-lg border bg-white px-5 pt-6 pb-4 space-y-4">
              <div className="flex items-start space-x-6">
                <div className="w-full space-y-1">
                  <Text variant="heading-md" as="h2">
                    Data Penilaian Kinerja
                  </Text>
                  <Text color="subdued">
                    {isPriorTo2025
                      ? 'Predikat Kinerja otomatis terisi berdasarkan rating. '
                      : 'Pilih Rating dan kirim ke Dinas Pendidikan agar Predikat Kinerja Organisasi satuan pendidikan Anda dapat diproses. '}
                    {isDirty
                      ? 'Status: Ada perubahan oleh Anda, mohon simpan draf.'
                      : 'Status: Belum ada perubahan.'}
                  </Text>

                  {isLoadingPelaksanaanConfig ? (
                    <Shimmer width={280} height={40} />
                  ) : (
                    <CardDeadlinePeriod deadlineDate={formattedDeadlineDate} />
                  )}
                </div>

                <div className="space-x-3 flex flex-shrink-0 items-start">
                  <Button
                    color="white"
                    size="lg"
                    isLoading={upsertPredikatKinerjaMutation.isLoading}
                    disabled={isDraftDisabled}
                    onClick={() => {
                      handleSaveToDraft()

                      trackEvent({
                        activity_type: 'penilaian_ks_skp_click_simpan_draf',
                        screen_name: 'ks_periksa_skp',
                        event_trigger: 'ks_penilaian_hub',
                      })
                    }}
                  >
                    Simpan draf
                  </Button>

                  {isAllItemsRated ? (
                    <Button
                      className="space-x-1"
                      size="lg"
                      disabled={isSubmitDisabled}
                      onClick={() => {
                        setActiveDialog('SUBMISSION')

                        trackEvent({
                          activity_type:
                            'penilaian_ks_skp_click_kirim_ke_dinas',
                          screen_name: 'ks_periksa_skp',
                          event_trigger: 'ks_penilaian_hub',
                        })
                      }}
                    >
                      <Icon color="inverse">send</Icon>
                      <span>Kirim ke Dinas Pendidikan</span>
                    </Button>
                  ) : (
                    <Tooltip content="Pilih rating untuk semua Guru terlebih dahulu">
                      <Button className="space-x-1" size="lg" disabled={true}>
                        <Icon color="inverse">send</Icon>
                        <span>Kirim ke Dinas Pendidikan</span>
                      </Button>
                    </Tooltip>
                  )}
                </div>
              </div>

              <dl className="flex space-x-3 w-full">
                {Object.entries(predikatKinerjaCounts).map(([key, count]) => (
                  <div
                    key={key}
                    className="border border-subdued rounded p-3 w-full space-y-1"
                  >
                    <Text as="dt" variant="action" className="!text-blue-60">
                      {PREDICATE_RECOMMENDATION_RESULT_TEXT[key]}
                    </Text>
                    <Text as="dd" variant="body-sm" color="subdued">
                      <span className="mr-10 inline-block">Jumlah</span>
                      <span>{count}</span>
                    </Text>
                  </div>
                ))}
              </dl>
            </div>

            <TablePenilaianKinerja
              showNilaiAndPoinPengembanganColumns={isPriorTo2025}
              isLoading={isLoadingAllData}
              items={completedItems}
              renderAction={(item) => {
                return (
                  <Link
                    href={`${PENGELOLAAN_KINERJA_KS_PENILAI_PATH}/penilaian-kinerja/${npsn}/daftar-guru/rangkuman-penilaian-kinerja/${item.ptkId}`}
                    passHref
                  >
                    <Button
                      size="sm"
                      color="white"
                      as="a"
                      type={undefined}
                      className="whitespace-nowrap"
                    >
                      Cek Rincian
                    </Button>
                  </Link>
                )
              }}
              onChange={(items) => {
                setCompletedItems(items)
                setIsDirty(true)
              }}
            />
          </section>

          {uncompletedItems.length > 0 && (
            <section>
              <CardCollapsible>
                <CollapsibleTrigger type="button">
                  <div className="space-x-3 flex completedItems-center">
                    <Icon>info_outlined</Icon>
                    <Text variant="heading-sm" as="h2">
                      {uncompletedItems.length} Pelaksanaan belum selesai
                    </Text>
                  </div>
                  <CollapsibleIcon />
                </CollapsibleTrigger>
                <CollapsibleContent className="p-4">
                  <ul className="list-disc list-inside pl-10">
                    {uncompletedItems.map((item) => (
                      <Text as="li" color="subdued" key={item.ptkId}>
                        {item.name}
                      </Text>
                    ))}
                  </ul>

                  <div className="pl-10 mt-6">
                    <Text variant="body-bold" color="subdued" as="h3">
                      Perlu diketahui bahwa:
                    </Text>

                    <ol className="list-decimal list-outside pl-5">
                      <Text as="li" color="subdued">
                        Anda dapat tetap lanjut ke tahap berikutnya untuk Guru
                        yang sudah selesai dinilai.
                      </Text>
                      <Text as="li" color="subdued">
                        Jika ada Guru yang belum selesai dinilai, Anda dapat
                        melakukan penilaian dan penetapannya di tahap{' '}
                        <strong>Penilaian Susulan.</strong>
                      </Text>
                      <Text as="li" color="subdued">
                        Jika ada Guru yang sudah tidak aktif (pensiun, meninggal
                        dunia, mutasi, dsb.) atau nama yang terduplikasi, mohon
                        kunjungi&nbsp;
                        <a
                          className="!text-blue-70 underline font-bold"
                          href={PANDUAN_PENGELOLAAN_URL}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Pusat Bantuan.
                        </a>
                      </Text>
                    </ol>
                  </div>
                </CollapsibleContent>
              </CardCollapsible>
            </section>
          )}
        </div>
      </main>

      <SubmitConfirmationDialog
        isOpen={activeDialog === 'SUBMISSION'}
        isLoading={upsertPredikatKinerjaMutation.isLoading}
        uncompletedItems={uncompletedItems.map((item) => item.name)}
        onClose={() => {
          setActiveDialog('NONE')
        }}
        onConfirm={handleSubmission}
      />

      <DialogSaveDraft
        data-testid="dialog-save-draft"
        isOpen={activeDialog === 'SAVE_TO_DRAFT'}
        onClickConfirm={handleSaveToDraft}
        onClickCancel={() => setActiveDialog('NONE')}
        onClose={() => setActiveDialog('NONE')}
      />

      <SuccessDialog
        isOpen={activeDialog === 'SUBMISSION_SUCCESS'}
        onClose={() => {
          queryClient.invalidateQueries(
            `${API_PATH_PREDIKAT_KINERJA_STEP}/${npsn}`
          )
          queryClient.invalidateQueries(
            `${API_PATH_PREDIKAT_KINERJA_LIST}/${npsn}`
          )

          if (isDemoMode) {
            queryClient.invalidateQueries(
              'demo-getPenetapanPredikatKinerjaStep'
            )
          }

          router.replace(
            `${PENGELOLAAN_KINERJA_KS_PENILAI_PATH}/penilaian-kinerja/${npsn}`
          )
        }}
      />
    </>
  )
}

const SUBMISSION_CHECKLIST: Array<{ label: string; value: string }> = [
  {
    value: '1',
    label:
      'Pengiriman dilakukan agar Predikat Kinerja Organisasi dapat diproses',
  },
  {
    value: '2',
    label: 'Pastikan semua guru sudah masuk ke dalam data Penilaian Kinerja',
  },
  {
    value: '3',
    label:
      'Data masih dapat Anda ubah setelah Predikat Kinerja Organisasi sudah diproses',
  },
]
const MAX_DISPLAYED_ITEMS = 3

type SubmitConfirmationDialogProps = {
  isOpen: boolean
  isLoading: boolean
  uncompletedItems: Array<string>
  onClose: () => void
  onConfirm: () => void
}

function SubmitConfirmationDialog({
  isOpen,
  uncompletedItems,
  isLoading,
  onClose,
  onConfirm,
}: SubmitConfirmationDialogProps) {
  const { trackEvent } = useSKPPelaksanaanTrackEvent()
  const [step, setStep] = useState<'UNCOMPLETED' | 'SUBMISSION'>('UNCOMPLETED')
  const [submissionChecklist, setSubmissionChecklist] = useState<Array<string>>(
    []
  )

  const uncompletedItemsLength = uncompletedItems.length
  const remainingLength = uncompletedItemsLength - MAX_DISPLAYED_ITEMS
  const isSubmissionConfirmed =
    submissionChecklist.length === SUBMISSION_CHECKLIST.length

  // Skip uncompleted items confirmation if necesarry
  useEffect(() => {
    setStep(uncompletedItemsLength > 0 ? 'UNCOMPLETED' : 'SUBMISSION')
  }, [uncompletedItemsLength])

  // Reset confirmation state upon closing the dialog
  useEffect(() => {
    if (!isOpen) {
      setSubmissionChecklist([])
    }
  }, [isOpen])

  return (
    <Dialog
      isOpen={isOpen}
      closeOnOverlayClick={false}
      size="sm"
      onClose={onClose}
    >
      <DialogOverlay />
      <DialogContent>
        {step === 'UNCOMPLETED' && (
          <div className="p-6 space-y-4">
            <Icon
              className="!w-20 !h-20 !text-7xl flex items-center justify-center"
              color="critical"
            >
              rule
            </Icon>
            <Text variant="display-sm" as="h2">
              Ada Guru yang belum selesai dinilai. Tetap lanjut?
            </Text>
            <div>
              <Text color="subdued" variant="body-lg">
                Pastikan nama berikut aktif dan Anda akan nilai nanti:
              </Text>
              <ul className="list-disc pl-6 overflow-wrap-anywhere">
                {uncompletedItems
                  .slice(0, MAX_DISPLAYED_ITEMS - 1)
                  .map((name, i) => (
                    <Text as="li" variant="body-lg" color="subdued" key={i}>
                      {name}
                    </Text>
                  ))}
                {remainingLength === 0 && (
                  <Text as="li" variant="body-lg" color="subdued">
                    {uncompletedItems[MAX_DISPLAYED_ITEMS - 1]}
                  </Text>
                )}
                {remainingLength >= 1 && (
                  <Text as="li" variant="body-lg" color="subdued">
                    {remainingLength + 1} orang lainnya
                  </Text>
                )}
              </ul>
            </div>

            <div className="flex space-x-4">
              <Button
                color="white"
                size="lg"
                onClick={() => {
                  trackEvent({
                    activity_type: 'penilaian_ks_skp_click_popup_kembali',
                    screen_name: 'ks_periksa_skp',
                    event_trigger: 'ks_penilaian_hub',
                  })

                  onClose()
                }}
              >
                Kembali
              </Button>
              <Button
                color="blue"
                size="lg"
                type="button"
                onClick={() => {
                  trackEvent({
                    activity_type: 'penilaian_ks_skp_click_popup_lanjut',
                    screen_name: 'ks_periksa_skp',
                    event_trigger: 'ks_penilaian_hub',
                  })
                  setStep('SUBMISSION')
                }}
              >
                Lanjut
              </Button>
            </div>
          </div>
        )}

        {step === 'SUBMISSION' && (
          <div className="p-6 space-y-4">
            <img
              src={assetUrl('/illustrations/img-publish-data.png')}
              alt=""
              width={84}
              height={84}
            />
            <Text variant="display-sm" as="h2">
              Konfirmasi untuk Kirim Data Penilaian
            </Text>
            <CheckboxGroup
              name="syarat"
              className="flex flex-col gap-2"
              value={submissionChecklist}
              onChange={setSubmissionChecklist}
            >
              {SUBMISSION_CHECKLIST.map((checkItem) => (
                <Checkbox value={checkItem.value} key={checkItem.value}>
                  {checkItem.label}
                </Checkbox>
              ))}
            </CheckboxGroup>

            <div className="flex space-x-4">
              <Button
                color="white"
                disabled={isLoading}
                size="lg"
                onClick={() => {
                  trackEvent({
                    activity_type: 'penilaian_ks_skp_click_popup_kembali',
                    screen_name: 'ks_periksa_skp',
                    event_trigger: 'ks_penilaian_hub',
                  })

                  if (uncompletedItemsLength > 0) {
                    setStep('UNCOMPLETED')
                  } else {
                    onClose()
                  }
                }}
              >
                Kembali
              </Button>
              <Button
                color="blue"
                disabled={!isSubmissionConfirmed || isLoading}
                isLoading={isLoading}
                size="lg"
                type="button"
                onClick={() => {
                  trackEvent({
                    activity_type: 'penilaian_ks_skp_click_popup_lanjut',
                    screen_name: 'ks_periksa_skp',
                    event_trigger: 'ks_penilaian_hub',
                  })

                  onConfirm()
                }}
              >
                Konfirmasi
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

type SuccessDialogProps = {
  isOpen: boolean
  onClose?: () => void
}

function SuccessDialog({ isOpen, onClose }: SuccessDialogProps) {
  return (
    <Dialog
      isOpen={isOpen}
      closeOnOverlayClick={false}
      size="sm"
      onClose={onClose}
    >
      <DialogOverlay />
      <DialogContent>
        <DialogCloseButton onClick={onClose} />
        <div className="p-6">
          <img
            src={assetUrl('/illustrations/img-thumbs-up.png')}
            alt=""
            width={84}
            height={84}
            className="mb-4"
          />

          <div className="mb-6">
            <Text variant="display-sm" as="h2" className="mb-2">
              Berhasil kirim Data Penilaian Kinerja!
            </Text>

            <Text variant="body-lg" color="subdued">
              Anda dapat konfirmasi ke Dinas Pendidikan untuk memproses Predikat
              Kinerja Organisasi.
            </Text>
          </div>

          <Button
            as="a"
            className="!inline-block"
            color="white"
            href={PENGELOLAAN_KINERJA_KS_PEGAWAI_PATH}
            size="lg"
            target="_blank"
            type="undefined"
          >
            Cek halaman konfirmasi data
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

const FAQS = [
  {
    title: 'Apakah Penilaian Kinerja dapat diubah lagi setelah dikirim?',
    description:
      'Ya. Anda dapat mengubah Penilaian Kinerja setelah data dikirim ke Dinas Pendidikan dan Predikat Kinerja Organisasi sudah diproses.',
  },
  {
    title: 'Apa saja data yang diterima oleh Dinas Pendidikan?',
    description:
      'Dinas Pendidikan akan menerima Predikat Kinerja Organisasi setiap satuan pendidikan berdasarkan rekomendasi dari Kemendikbudristek. Dinas Pendidikan tidak akan menerima rincian data Penilaian Kinerja.',
  },
  {
    title: 'Apa pengertian dari Nilai?',
    description:
      'Nilai adalah angka yang otomatis dihasilkan dari penilaian Anda di tahap sebelumnya. Nilai dapat membantu Anda dalam menetapkan rating tiap guru.',
  },
  {
    title: 'Apakah nilai dapat diubah?',
    description:
      'Tidak. Anda hanya dapat mengubah rating. Jika Anda mengubah rating, nilai tidak akan ikut terubah. Tidak perlu khawatir, nilai yang tidak berubah ini tidak akan berpengaruh terhadap rating yang sudah Anda tentukan.',
  },
]

function FaqsButton() {
  const [isOpen, setOpen] = useState(false)

  return (
    <>
      <Button
        variant="ghost"
        className="space-x-1 !bg-white"
        size="lg"
        onClick={() => setOpen(true)}
      >
        <Icon color="inherit">help</Icon>
        <span>Baca selengkapnya</span>
      </Button>
      <Dialog isOpen={isOpen} size="md">
        <DialogOverlay />
        <DialogContent>
          <DialogHeader
            title="Tanya Jawab tentang Pengiriman Data Penilaian Kinerja"
            borderless={true}
          />
          <div className="p-6">
            <ul className="mb-2">
              {FAQS.map((step, i) => {
                return (
                  <li key={i} className="flex space-x-4">
                    <div
                      className="w-6 h-6 rounded-full bg-gray-80 flex-none flex items-center justify-center"
                      role="presentation"
                    >
                      <Text variant="body-sm" color="inverse">
                        {i + 1}
                      </Text>
                    </div>
                    <div className="mb-4">
                      <Text as="h3" variant="action" className="mb-1">
                        {step.title}
                      </Text>
                      <Text color="subdued">{step.description}</Text>
                    </div>
                  </li>
                )
              })}
            </ul>
            <Button
              size="lg"
              className="mx-auto"
              onClick={() => setOpen(false)}
            >
              Saya mengerti
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
